import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { LiveClient, LiveState } from './client.ts';

// The live connection's state for the shell (design §6.5): the banner, and the
// server's clock for pages that show countdowns.

/** How long the stream must be down before the shell says "Reconnecting…". */
export const RECONNECTING_DELAY_MS = 3_000;

export const useConnectionStore = defineStore('itc-connection', () => {
  const state = ref<LiveState>('idle');
  const workerConfigured = ref(false);
  const workerConnected = ref(false);
  const clockOffsetMs = ref(0);
  /** True once the stream has been down for RECONNECTING_DELAY_MS. */
  const reconnecting = ref(false);
  let timer: ReturnType<typeof setTimeout> | undefined;
  let unbind: (() => void) | undefined;

  function setState(next: LiveState): void {
    state.value = next;
    if (next === 'reconnecting') {
      timer ??= setTimeout(() => {
        reconnecting.value = true;
      }, RECONNECTING_DELAY_MS);
    } else {
      clearTimeout(timer);
      timer = undefined;
      reconnecting.value = false;
    }
  }

  /** Follows a client's state, worker status and clock. */
  function bind(client: LiveClient): void {
    unbind?.();
    setState(client.state);
    const offs = [
      client.onState((next) => {
        setState(next);
        clockOffsetMs.value = client.clockOffsetMs;
      }),
      client.onWorker((worker) => {
        workerConfigured.value = worker.configured;
        workerConnected.value = worker.connected;
        clockOffsetMs.value = client.clockOffsetMs;
      }),
    ];
    // Pings move the clock without changing state; sample it when asked instead.
    clockSource = () => client.clockOffsetMs;
    unbind = () => {
      for (const off of offs) off();
    };
  }

  let clockSource: (() => number) | undefined;

  /** The server's time now, from the offset measured on the stream. */
  function serverNow(): Date {
    return new Date(Date.now() + (clockSource?.() ?? clockOffsetMs.value));
  }

  /** "Discord is unavailable": a worker is configured but none is connected. */
  const discordUnavailable = computed(() => state.value === 'open' && workerConfigured.value && !workerConnected.value);

  return { state, workerConfigured, workerConnected, clockOffsetMs, reconnecting, discordUnavailable, bind, serverNow };
});
